import { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowRight, ArrowLeft, Sparkles, Send, Loader2, Headphones,
  PhoneCall, Inbox, Users, Mic, Briefcase, BellRing,
  RotateCcw, ChevronRight, MessageSquare,
} from "lucide-react";
import { KaiMarkdown } from "@/components/landing/KaiMarkdown";
import { VoiceAIModal } from "@/components/landing/VoiceAIModal";
import {
  ScenarioReceptionist, ScenarioLeadIntent, ScenarioInbox,
  ScenarioPatient, ScenarioCallIQ, ScenarioAlerts,
} from "@/components/landing/KaiScenarios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/* Tiny inline component for the Ask-KAI scenario — intro card that sits above
   the chat panel (which renders automatically because showChat is true). */
const ScenarioAskKai = ({ accent }) => {
  const gradient = `linear-gradient(135deg, ${accent.from}, ${accent.to})`;
  return (
    <div className="rounded-3xl border border-border bg-card p-6 shadow-sm sm:p-8">
      <div className="flex items-start gap-4">
        <span className="inline-flex h-12 w-12 flex-none items-center justify-center rounded-2xl text-white shadow-md ring-1 ring-inset ring-white/15" style={{ backgroundImage: gradient }}>
          <Sparkles className="h-5 w-5" />
        </span>
        <div>
          <p className="text-[10.5px] font-bold uppercase tracking-[0.22em]" style={{ color: accent.from }}>
            Ask KAI · stand-alone
          </p>
          <h3 className="mt-1 font-display text-2xl font-black tracking-tight sm:text-3xl">
            Free-form chat with KAI.
          </h3>
          <p className="mt-2 max-w-2xl text-[13.5px] leading-relaxed text-muted-foreground">
            Ask KAI anything about running a clinic — pricing, scripts, KPIs, scheduling logic, no-show tactics. KAI is your operations co-pilot — chat below uses the live Claude model and remembers the thread.
          </p>
        </div>
      </div>
    </div>
  );
};

/* Scenario registry — each scenario points to its own UI component,
   and gives KAI a CONTEXT block so the chat (when shown) stays grounded. */
const SCENARIOS = {
  reception: {
    id: "reception",
    label: "AI Receptionist · 3 rings",
    short: "3 rings, AI picks up · collects name/email/phone/apt",
    Icon: PhoneCall,
    accent: { from: "#0ea5e9", to: "#2563eb" },
    Component: ScenarioReceptionist,
    showChat: false,
    suggestions: [],
    context: () =>
      `KAI just answered an inbound call after 3 rings at Apex Wellness — Beachview.\n` +
      `Caller: Daniel Brooks · email daniel.brooks@gmail.com · phone +1 (416) 555-0188\n` +
      `Booked: Tuesday 2:00 PM with Sarah Chen · 60-min physio assessment.\n` +
      `Reason: Lower-back pain · post-hike. Confirmation SMS already sent.`,
  },
  lead: {
    id: "lead",
    label: "Lead intelligence",
    short: "Leads table · KAI summary, next action, last touch, factored notes",
    Icon: Briefcase,
    accent: { from: "#7c3aed", to: "#a855f7" },
    Component: ScenarioLeadIntent,
    showChat: false,
    suggestions: [],
    context: () =>
      `Leads table at Apex Wellness · Ana Canabrava row open.\n` +
      `KAI Intelligence active for this lead — last touched 2 days ago via outbound call, no reply.`,
  },
  inbox: {
    id: "inbox",
    label: "Inbox AI Summary",
    short: "Patient SMS · click AI Summary · drafted reply + snippets",
    Icon: Inbox,
    accent: { from: "#a855f7", to: "#d946ef" },
    Component: ScenarioInbox,
    showChat: false,
    suggestions: [],
    context: () =>
      `Patient SMS thread at Apex Wellness — Lakeshore. Eleanor Park, 14 prior visits. 3 messages, escalating, SLA breach.`,
  },
  patient: {
    id: "patient",
    label: "Patient insights · timeline",
    short: "Profile + activity trail · KAI scores risk + next-best-action",
    Icon: Users,
    accent: { from: "#10b981", to: "#14b8a6" },
    Component: ScenarioPatient,
    showChat: false,
    suggestions: [],
    context: () =>
      `Patient profile open at Apex Wellness — Beachview. Jordan Kim. 6 visits / 90 days. Outstanding $48.50. KAI flagged HIGH retention risk.`,
  },
  calliq: {
    id: "calliq",
    label: "Call IQ · classify a call",
    short: "Transcript + AI Classification (booked · human · existing · agent)",
    Icon: Mic,
    accent: { from: "#0284c7", to: "#3b82f6" },
    Component: ScenarioCallIQ,
    showChat: false,
    suggestions: [],
    context: () =>
      `Inbound call · Mar 11 9:14 AM · Unknown +1 (416) 728-4407. Meghan Watson answered. Classified booked / existing / New Assessment.`,
  },
  alerts: {
    id: "alerts",
    label: "KAI Alerts · live feed",
    short: "Pending review · stale leads · SLA breaches · social DMs",
    Icon: BellRing,
    accent: { from: "#7c3aed", to: "#ec4899" },
    Component: ScenarioAlerts,
    showChat: false,
    suggestions: [],
    context: () =>
      `KAI alert feed. 1 pending review, 1 stale lead, 1 SLA breach, 1 Instagram DM, 1 email queue warning.`,
  },
  ask: {
    id: "ask",
    label: "Ask KAI · free-form",
    short: "Chat with KAI about anything — pricing, KPIs, scripts, no-show tactics",
    Icon: MessageSquare,
    accent: { from: "#8b5cf6", to: "#6366f1" },
    Component: ScenarioAskKai,
    showChat: true,
    suggestions: [
      "What KPIs should a multi-location PT chain track weekly?",
      "Write a 3-step SMS recall sequence for lapsed patients",
      "How should I price an introductory consult?",
      "Best way to reduce no-shows without annoying patients?",
    ],
    context: () => `General Q&A — no scenario context. Speak as KAI to a clinic operator or owner.`,
  },
};

const SCENARIO_LIST = ["reception", "lead", "inbox", "patient", "calliq", "alerts", "ask"];

export default function KaiExperience() {
  const [scenarioId, setScenarioId] = useState("reception");
  const [sessionId, setSessionId] = useState(() => crypto.randomUUID());
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [showVoice, setShowVoice] = useState(false);

  const scenario = SCENARIOS[scenarioId];
  const Component = scenario.Component;
  const chatEndRef = useRef(null);
  const gradient = `linear-gradient(135deg, ${scenario.accent.from}, ${scenario.accent.to})`;

  const switchScenario = (id) => {
    if (id === scenarioId) return;
    setScenarioId(id);
    setMessages([]);
    setSessionId(crypto.randomUUID());
    setErr("");
  };

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [messages, busy]);

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || busy) return;
    setMessages((m) => [...m, { role: "user", text: msg }]);
    setInput("");
    setBusy(true);
    setErr("");
    try {
      const res = await fetch(`${API}/kai/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          session_id: sessionId,
          scenario: scenario.id,
          context: scenario.context(),
          message: msg,
        }),
      });
      if (!res.ok) throw new Error("HTTP " + res.status);
      const data = await res.json();
      setMessages((m) => [...m, { role: "kai", text: data.reply || "—" }]);
    } catch (_e) {
      setErr("KAI is offline right now — please try again in a moment.");
    } finally {
      setBusy(false);
    }
  };

  const reset = () => {
    setMessages([]);
    setSessionId(crypto.randomUUID());
    setErr("");
  };

  return (
    <article data-testid="kai-experience" className="overflow-hidden">

      {/* HERO */}
      <section className="relative pt-16 pb-4 sm:pt-28">
        <div className="pointer-events-none absolute inset-x-0 top-0 -z-10 h-72 bg-gradient-to-b from-violet-500/15 to-transparent" />
        <div className="mx-auto max-w-7xl px-5 sm:px-8 lg:px-12">
          <Link to="/kai" data-testid="kai-experience-back" className="inline-flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-[0.18em] text-muted-foreground transition-colors hover:text-foreground">
            <ArrowLeft className="h-3 w-3" />
            Back to KAI
          </Link>
          <div className="mt-4 sm:mt-5 flex flex-col items-start justify-between gap-4 sm:gap-5 sm:flex-row sm:items-end">
            <div className="max-w-2xl">
              <span className="hidden sm:inline-flex items-center gap-1.5 rounded-full border border-violet-500/30 bg-violet-500/10 px-3 py-1 text-[11px] font-bold uppercase tracking-[0.18em] text-violet-600 dark:text-violet-300">
                <Sparkles className="h-3 w-3" />
                Live · 7 KAI moments
              </span>
              <h1 className="mt-0 sm:mt-4 font-display text-[1.9rem] font-black leading-[1.05] tracking-tight sm:text-4xl lg:text-5xl">
                See KAI{" "}
                <span className="bg-gradient-to-r from-violet-500 via-fuchsia-500 to-rose-500 bg-clip-text text-transparent">do the work</span>{" "}
                — seven real moments inside a clinic.
              </h1>
              <p className="mt-2.5 sm:mt-3 text-[13.5px] leading-relaxed text-muted-foreground sm:text-base">
                Each tab is a different KAI intelligence at work — receive a call, score a lead, draft a reply, classify a transcript, surface alerts, analyse a patient, or just chat free-form. Real product UIs · mock data.
              </p>
            </div>

            <div className="flex w-full flex-wrap gap-2 sm:w-auto">
              <button
                type="button"
                onClick={() => setShowVoice(true)}
                data-testid="kai-voice-open"
                className="group inline-flex h-11 w-full sm:w-auto items-center justify-center gap-1.5 rounded-xl bg-gradient-to-r from-violet-600 to-fuchsia-600 px-4 text-[12.5px] font-bold text-white shadow-md ring-1 ring-inset ring-white/15 transition-all hover:-translate-y-0.5 hover:shadow-lg"
              >
                <Headphones className="h-3.5 w-3.5" />
                Talk to KAI by voice
                <span className="ml-1 rounded-full bg-white/20 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-[0.12em]">4 industries</span>
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* SCENARIO PICKER — bigger cards now since each is a different demo */}
      <section className="border-t border-border py-5">
        <div className="mx-auto max-w-7xl px-6 sm:px-8 lg:px-12">
          <div className="-mx-6 overflow-x-auto px-6 sm:mx-0 sm:overflow-visible sm:px-0">
            <div className="flex w-max gap-2 sm:w-auto sm:flex-wrap" data-testid="kai-scenarios">
              {SCENARIO_LIST.map((id) => {
                const s = SCENARIOS[id];
                const active = scenarioId === id;
                return (
                  <button
                    key={id}
                    type="button"
                    onClick={() => switchScenario(id)}
                    data-testid={`kai-scenario-${id}`}
                    aria-pressed={active}
                    style={active ? { backgroundImage: `linear-gradient(135deg, ${s.accent.from}, ${s.accent.to})` } : undefined}
                    className={
                      active
                        ? "inline-flex max-w-xs flex-none flex-col items-start gap-0.5 rounded-2xl px-4 py-2.5 text-left text-white shadow-md ring-1 ring-inset ring-white/15 transition-all"
                        : "inline-flex max-w-xs flex-none flex-col items-start gap-0.5 rounded-2xl border border-border bg-card px-4 py-2.5 text-left text-foreground/80 transition-all hover:-translate-y-0.5 hover:border-foreground/30"
                    }
                  >
                    <span className="inline-flex items-center gap-1.5 text-[11.5px] font-bold tracking-tight">
                      <s.Icon className="h-3.5 w-3.5" />
                      {s.label}
                    </span>
                    <span className={`text-[10.5px] leading-snug ${active ? "text-white/85" : "text-muted-foreground"}`}>
                      {s.short}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* SCENARIO BODY — full-width, animated swap */}
      <section className="pb-12 pt-8 sm:pb-16">
        <div className="mx-auto max-w-7xl px-6 sm:px-8 lg:px-12">
          <AnimatePresence mode="wait">
            <motion.div
              key={scenario.id}
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.3 }}
            >
              <Component accent={scenario.accent} />
            </motion.div>
          </AnimatePresence>
        </div>
      </section>

      {/* ASK-KAI CHAT — only for scenarios where chat adds depth */}
      {scenario.showChat && (
        <section className="border-t border-border bg-secondary/30 py-12 dark:bg-transparent sm:py-16" data-testid="kai-chat-section">
          <div className="mx-auto max-w-5xl px-6 sm:px-8 lg:px-12">
            <div className="mb-4 flex flex-col items-start justify-between gap-2 sm:flex-row sm:items-end">
              <div>
                <p className="text-[10.5px] font-bold uppercase tracking-[0.22em]" style={{ color: scenario.accent.from }}>
                  Chat with KAI
                </p>
                <h2 className="mt-1 font-display text-2xl font-black tracking-tight sm:text-3xl">
                  Ask anything — no scenario, no rails.
                </h2>
              </div>
              <button type="button" onClick={reset} className="inline-flex h-9 items-center gap-1.5 rounded-lg border border-border bg-card px-3 text-[12px] font-semibold transition-all hover:-translate-y-0.5 hover:border-foreground/30">
                <RotateCcw className="h-3 w-3" />
                Reset chat
              </button>
            </div>

            <div className="overflow-hidden rounded-3xl border border-border bg-card shadow-sm">
              <div className="flex items-center gap-2.5 border-b border-border bg-gradient-to-r from-violet-500/[0.10] to-fuchsia-500/[0.10] px-5 py-3.5">
                <span className="inline-flex h-9 w-9 items-center justify-center rounded-xl bg-gradient-to-br from-violet-600 to-fuchsia-600 text-white shadow-sm ring-1 ring-inset ring-white/15">
                  <Sparkles className="h-4 w-4" />
                </span>
                <div className="flex-1">
                  <p className="font-display text-[14px] font-black tracking-tight">KAI</p>
                  <p className="text-[10.5px] text-muted-foreground">Live · Claude · grounded in the scenario above</p>
                </div>
                <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-500/15 px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.12em] text-emerald-600 dark:text-emerald-400">
                  <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
                  Online
                </span>
              </div>

              <div className="h-[320px] overflow-y-auto px-5 py-4" data-testid="kai-messages">
                {messages.length === 0 && (
                  <div className="flex h-full flex-col items-center justify-center text-center">
                    <span className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-violet-500/15 text-violet-600 dark:text-violet-300">
                      <MessageSquare className="h-6 w-6" />
                    </span>
                    <p className="mt-4 max-w-md text-[13px] leading-relaxed text-muted-foreground">
                      Pick a suggested question below — KAI will answer using the scenario context.
                    </p>
                  </div>
                )}

                {messages.map((m, i) => (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, y: 6 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.2 }}
                    className={`mb-3 flex ${m.role === "user" ? "justify-end" : "justify-start"}`}
                  >
                    <div
                      className={
                        m.role === "kai"
                          ? "max-w-[88%] rounded-2xl rounded-tl-sm border border-border bg-secondary/40 px-4 py-3 text-[13.5px] leading-relaxed"
                          : "max-w-[88%] whitespace-pre-wrap rounded-2xl rounded-tr-sm px-4 py-3 text-[13.5px] font-medium leading-relaxed text-white shadow-md"
                      }
                      style={m.role === "user" ? { backgroundImage: gradient } : undefined}
                      data-testid={`kai-msg-${m.role}-${i}`}
                    >
                      {m.role === "kai" ? <KaiMarkdown text={m.text} /> : m.text}
                    </div>
                  </motion.div>
                ))}

                {busy && (
                  <div className="mb-3 flex justify-start">
                    <div className="inline-flex items-center gap-2 rounded-2xl rounded-tl-sm border border-border bg-secondary/40 px-4 py-3 text-[12.5px] text-muted-foreground">
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      KAI is thinking…
                    </div>
                  </div>
                )}

                {err && <p className="text-center text-[12px] font-semibold text-rose-500">{err}</p>}
                <div ref={chatEndRef} />
              </div>

              <div className="border-t border-border bg-secondary/20 px-5 py-3">
                <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-muted-foreground">Suggested questions</p>
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {scenario.suggestions.map((q) => (
                    <button
                      key={q}
                      type="button"
                      onClick={() => send(q)}
                      disabled={busy}
                      className="inline-flex items-center gap-1 rounded-full border border-border bg-card px-2.5 py-1 text-[11.5px] font-semibold text-foreground/80 transition-all hover:-translate-y-0.5 hover:border-violet-500/40 hover:text-foreground disabled:opacity-50"
                    >
                      {q}
                      <ChevronRight className="h-3 w-3 opacity-50" />
                    </button>
                  ))}
                </div>
              </div>

              <form onSubmit={(e) => { e.preventDefault(); send(); }} className="flex items-center gap-2 border-t border-border bg-card px-3 py-3">
                <input
                  type="text"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  placeholder="Ask KAI about this scenario…"
                  data-testid="kai-input"
                  className="flex-1 rounded-lg border border-border bg-background px-3 py-2.5 text-sm outline-none ring-ring transition-all focus:ring-2"
                />
                <button
                  type="submit"
                  disabled={busy || !input.trim()}
                  data-testid="kai-send"
                  style={{ backgroundImage: gradient }}
                  className="inline-flex h-10 flex-none items-center justify-center gap-1.5 rounded-lg px-4 text-[13px] font-bold text-white shadow-md transition-all hover:-translate-y-0.5 hover:shadow-lg disabled:opacity-50"
                >
                  Send <Send className="h-3.5 w-3.5" />
                </button>
              </form>
            </div>
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="border-t border-border py-16">
        <div className="mx-auto max-w-4xl px-6 text-center sm:px-8 lg:px-12">
          <h2 className="font-display text-3xl font-black tracking-tight sm:text-4xl">
            Want this <span className="bg-gradient-to-r from-violet-600 to-fuchsia-600 bg-clip-text text-transparent">working on your clinic</span>?
          </h2>
          <p className="mt-3 text-base leading-relaxed text-muted-foreground">
            Book a personalised walk-through and we&apos;ll show KAI handling calls, leads and reports from your actual EMR.
          </p>
          <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
            <Link to="/integrations/juvonno#book" data-testid="kai-experience-final-cta" className="group inline-flex h-12 items-center gap-2 rounded-xl bg-gradient-to-r from-violet-600 to-fuchsia-600 px-7 text-sm font-bold text-white shadow-lg ring-1 ring-inset ring-white/15 transition-all hover:-translate-y-0.5 hover:shadow-xl">
              Book a personalised walk-through
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
            </Link>
            <Link to="/kai" className="inline-flex h-12 items-center gap-2 rounded-xl border border-border bg-card px-7 text-sm font-semibold transition-all hover:-translate-y-0.5 hover:border-foreground/30">
              Read about KAI
            </Link>
          </div>
        </div>
      </section>

      <VoiceAIModal open={showVoice} onClose={() => setShowVoice(false)} scenarioLabel={scenario.label} />
    </article>
  );
}
